<template>
  <UModal v-model="isOpen" :ui="{ width: 'w-full max-w-2xl' }">
    <UCard>
      <template #header>
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold">Filters</h2>
          <UButton
            v-if="hasActiveFilters"
            variant="ghost"
            color="primary"
            @click="clearFilters"
          >
            Clear All
          </UButton>
        </div>
      </template>

      <div class="space-y-6">
        <!-- Price Range -->
        <div>
          <h3 class="text-lg font-semibold mb-4">Price Range</h3>
          <div class="flex gap-2 mb-4">
            <UInput
              v-model.number="localFilters.minPrice"
              type="number"
              :min="minPrice"
              :max="maxPrice"
              placeholder="Min Price"
              class="flex-1"
            />
            <UInput
              v-model.number="localFilters.maxPrice"
              type="number"
              :min="minPrice"
              :max="maxPrice"
              placeholder="Max Price"
              class="flex-1"
            />
          </div>
          <div class="px-2">
            <div class="flex justify-between mb-2">
              <span class="text-sm">{{ localFilters.minPrice ?? minPrice }} KWD</span>
              <span class="text-sm">{{ localFilters.maxPrice ?? maxPrice }} KWD</span>
            </div>
            <input
              type="range"
              :min="minPrice"
              :max="maxPrice"
              :step="100"
              :value="localFilters.minPrice ?? minPrice"
              class="w-full mb-2"
              @input="(e) => { localFilters.minPrice = Number((e.target as HTMLInputElement).value) }"
            />
            <input
              type="range"
              :min="minPrice"
              :max="maxPrice"
              :step="100"
              :value="localFilters.maxPrice ?? maxPrice"
              class="w-full"
              @input="(e) => { localFilters.maxPrice = Number((e.target as HTMLInputElement).value) }"
            />
          </div>
        </div>

        <!-- Days Range -->
        <div>
          <h3 class="text-lg font-semibold mb-4">Number of Days</h3>
          <div class="flex gap-2 mb-4">
            <UInput
              v-model.number="localFilters.minDays"
              type="number"
              :min="minDays"
              :max="maxDays"
              placeholder="Min Days"
              class="flex-1"
            />
            <UInput
              v-model.number="localFilters.maxDays"
              type="number"
              :min="minDays"
              :max="maxDays"
              placeholder="Max Days"
              class="flex-1"
            />
          </div>
          <div class="px-2">
            <div class="flex justify-between mb-2">
              <span class="text-sm">{{ localFilters.minDays ?? minDays }} days</span>
              <span class="text-sm">{{ localFilters.maxDays ?? maxDays }} days</span>
            </div>
            <input
              type="range"
              :min="minDays"
              :max="maxDays"
              :step="1"
              :value="localFilters.minDays ?? minDays"
              class="w-full mb-2"
              @input="(e) => { localFilters.minDays = Number((e.target as HTMLInputElement).value) }"
            />
            <input
              type="range"
              :min="minDays"
              :max="maxDays"
              :step="1"
              :value="localFilters.maxDays ?? maxDays"
              class="w-full"
              @input="(e) => { localFilters.maxDays = Number((e.target as HTMLInputElement).value) }"
            />
          </div>
        </div>

        <!-- Continents -->
        <div>
          <h3 class="text-lg font-semibold mb-4">Continents</h3>
          <div class="flex flex-wrap gap-2">
            <UButton
              v-for="continent in continents"
              :key="continent.id"
              :variant="localFilters.continentIds?.includes(continent.id) ? 'solid' : 'outline'"
              :color="localFilters.continentIds?.includes(continent.id) ? 'primary' : 'gray'"
              @click="toggleContinent(continent.id)"
            >
              {{ continent.name }}
            </UButton>
          </div>
        </div>

        <!-- Months -->
        <div>
          <h3 class="text-lg font-semibold mb-4">Months</h3>
          <div class="flex flex-wrap gap-2">
            <UButton
              v-for="month in months"
              :key="month.id"
              :variant="localFilters.monthIds?.includes(month.id) ? 'solid' : 'outline'"
              :color="localFilters.monthIds?.includes(month.id) ? 'primary' : 'gray'"
              @click="toggleMonth(month.id)"
            >
              {{ month.name }}
            </UButton>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <UButton variant="ghost" @click="close">Cancel</UButton>
          <UButton color="primary" @click="applyFilters">Apply Filters</UButton>
        </div>
      </template>
    </UCard>
  </UModal>
</template>

<script setup lang="ts">
import type { TripFilters } from "~/types/filter";

const props = defineProps<{
  modelValue: boolean;
  filters: TripFilters;
  minPrice?: number;
  maxPrice?: number;
  minDays?: number;
  maxDays?: number;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  "apply": [filters: TripFilters];
}>();

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const localFilters = ref<TripFilters>({ ...props.filters });

const continents = [
  { id: 1, name: "Africa" },
  { id: 2, name: "Antarctica" },
  { id: 3, name: "Asia" },
  { id: 4, name: "Europe" },
  { id: 5, name: "North America" },
  { id: 6, name: "Australia" },
  { id: 7, name: "South America" },
];

const months = [
  { id: 1, name: "January" },
  { id: 2, name: "February" },
  { id: 3, name: "March" },
  { id: 4, name: "April" },
  { id: 5, name: "May" },
  { id: 6, name: "June" },
  { id: 7, name: "July" },
  { id: 8, name: "August" },
  { id: 9, name: "September" },
  { id: 10, name: "October" },
  { id: 11, name: "November" },
  { id: 12, name: "December" },
];

const minPrice = computed(() => props.minPrice ?? 0);
const maxPrice = computed(() => props.maxPrice ?? 10000);
const minDays = computed(() => props.minDays ?? 1);
const maxDays = computed(() => props.maxDays ?? 30);

const hasActiveFilters = computed(() => {
  return (
    localFilters.value.minPrice != null ||
    localFilters.value.maxPrice != null ||
    localFilters.value.minDays != null ||
    localFilters.value.maxDays != null ||
    (localFilters.value.continentIds && localFilters.value.continentIds.length > 0) ||
    (localFilters.value.monthIds && localFilters.value.monthIds.length > 0)
  );
});

watch(
  () => props.filters,
  (newFilters) => {
    localFilters.value = { ...newFilters };
  },
  { deep: true }
);

function toggleContinent(id: number) {
  if (!localFilters.value.continentIds) {
    localFilters.value.continentIds = [];
  }
  const index = localFilters.value.continentIds.indexOf(id);
  if (index > -1) {
    localFilters.value.continentIds.splice(index, 1);
  } else {
    localFilters.value.continentIds.push(id);
  }
}

function toggleMonth(id: number) {
  if (!localFilters.value.monthIds) {
    localFilters.value.monthIds = [];
  }
  const index = localFilters.value.monthIds.indexOf(id);
  if (index > -1) {
    localFilters.value.monthIds.splice(index, 1);
  } else {
    localFilters.value.monthIds.push(id);
  }
}


function clearFilters() {
  localFilters.value = {};
}

function applyFilters() {
  emit("apply", { ...localFilters.value });
  isOpen.value = false;
}

function close() {
  localFilters.value = { ...props.filters };
  isOpen.value = false;
}
</script>

